import { store } from '../data/db.ts';

/**
 * Der Schlüssel für die Titel in Mitteilungen
 * (docs/KONZEPT-ERINNERUNGEN.md, Abschnitt 5.2).
 *
 * Bewusst **nicht** der Vault Key: Der Service Worker muss den Titel entziffern
 * können, während der Vault gesperrt ist — sonst stünde in jeder Mitteilung nur
 * „1 Erinnerung fällig". Der Preis steht im Konzept: Titel von Erinnerungen sind auf
 * dem Sperrbildschirm lesbar. Für Notizinhalte ändert sich nichts.
 *
 * Bewusst auch **nicht** libsodium, sondern AES-GCM aus der WebCrypto-Schnittstelle:
 * Der Service Worker müsste sonst ein WASM-Modul laden, bevor er die Mitteilung
 * anzeigen darf — und iOS verlangt, dass jede Push-Nachricht zu einer Mitteilung
 * führt. Weniger Teile auf diesem Weg sind mehr wert als ein einheitliches Verfahren.
 */

const IV_BYTES = 12;

async function keyBytes(): Promise<Uint8Array> {
  const existing = await store.getNotifyKey();
  if (existing) return existing;
  const fresh = crypto.getRandomValues(new Uint8Array(32));
  await store.setNotifyKey(fresh);
  return fresh;
}

async function aesKey(): Promise<CryptoKey> {
  const raw = await keyBytes();
  return crypto.subtle.importKey('raw', raw as BufferSource, 'AES-GCM', false, ['encrypt']);
}

/** Base64 ohne Umwege — die Werte sind wenige Dutzend Bytes. */
function toB64(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

/** Verschlüsselt den Titel für die Mitteilung: base64(iv ‖ Chiffrat). */
export async function sealTitle(title: string): Promise<string> {
  const iv = crypto.getRandomValues(new Uint8Array(IV_BYTES));
  const data = new TextEncoder().encode(title.slice(0, 200));
  const sealed = new Uint8Array(
    await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, await aesKey(), data),
  );
  const out = new Uint8Array(iv.length + sealed.length);
  out.set(iv);
  out.set(sealed, iv.length);
  return toB64(out);
}

/** Stellt sicher, dass es den Schlüssel gibt, bevor Mitteilungen eingerichtet werden. */
export async function ensureNotifyKey(): Promise<void> {
  await keyBytes();
}
